import React, { useEffect, useMemo, useState } from "react";
import { useSelector } from "react-redux";
import { organizerBeautyStatsApi } from "../../api/api";
import { beautyGetSalonApi, beautyListProfessionalsApi, beautyMeApi } from "../../api/organizerBeautyApi";
import SpinLoading from "../../components/spinLoading/SpinLoading";
import StatsFilters, { periodFields } from "./StatsFilters";
import StatsTabs from "./StatsTabs";
import BeautyResumenTab from "./BeautyResumenTab";
import BeautyEvolucionTab from "./BeautyEvolucionTab";
import BeautyHorariosTab from "./BeautyHorariosTab";
import BeautyProfesionalesTab from "./BeautyProfesionalesTab";
import BeautyServiciosTab from "./BeautyServiciosTab";
import { StatsRangeCaption } from "./StatsPrimitives";
import { StatsTooltipProvider } from "./StatsTooltip";
import useStatsData from "./useStatsData";
import { beautyFileName, buildBeautySheets } from "./beautySheets";
import { downloadStatsWorkbook } from "./statsExport";
import { bucketSize, daysInRange, resolveComparison, resolvePeriod } from "./statsDates";
import { buildStatsParams, isLinkedScope, professionalFilterOptions } from "./beautyStatsMetrics";
import "./estadisticas.css";

/* ─────────────────────────────────────────
   EstadisticasBeauty — statistics of the organizer's salon (Beauty > Estadísticas).

   Data: GET /api/organizer/beauty/stats, called twice in parallel (current range and comparison
   range) through useStatsData. All the numbers are computed by the back; the pure helpers in
   beautyStats*.js only reshape them for display. A business has one salon, so there is no salon
   selector (the back resolves it from the token; `salonId` is optional there).

   A linked professional (an employee whose account is linked to a professional of the salon) gets
   only their own numbers from the back (`scope.type === "own"`): the screen hides the Profesional
   filter and the Por profesional tab, swaps "Ticket promedio" for "Tu comisión" and explains why.

   Tabs: Resumen, Evolución y canales, Horarios, Por profesional (not for linked professionals),
   Por servicio. The remembered tab has its own storage key, separate from the court screen's.
   ───────────────────────────────────────── */

const ALL_TABS = [
  { id: "resumen", label: "Resumen" },
  { id: "evolucion", label: "Evolución y canales" },
  { id: "horarios", label: "Horarios" },
  { id: "profesionales", label: "Por profesional" },
  { id: "servicios", label: "Por servicio" },
];

const COMPARE_LABELS = { prev: "vs período anterior", yoy: "vs año pasado" };

const INITIAL_FILTERS = { period: "last30", compare: "prev", professional: "all" };

const TAB_STORAGE_KEY = "zocoBeautyStatsTab";

export default function EstadisticasBeauty({ today: todayProp }) {
  // "Today" is read once: the ranges must not move under the user's feet while the page is open.
  const [today] = useState(() => todayProp || new Date());

  const isEmployee = useSelector((state) => state.auth)?.user?.tipo === "empleado";
  const [salon, setSalon] = useState(null);
  const [linkedProfessional, setLinkedProfessional] = useState(null);
  const [salonLoading, setSalonLoading] = useState(true);
  const [salonError, setSalonError] = useState("");
  const [professionals, setProfessionals] = useState([]);
  const [filters, setFilters] = useState(INITIAL_FILTERS);
  const [exportError, setExportError] = useState(false);

  // The salon and, for an employee, whether their account is linked to a professional (`/me`; a
  // failing call counts as "not linked", like the Turnos screen). Both decide what the page shows.
  useEffect(() => {
    let active = true;
    (async () => {
      try {
        const askMe = isEmployee
          ? beautyMeApi().then((r) => r?.data?.info?.professional ?? r?.data?.professional ?? null).catch(() => null)
          : Promise.resolve(null);
        const [res, me] = await Promise.all([beautyGetSalonApi(), askMe]);
        if (!active) return;
        setLinkedProfessional(me);
        setSalon(res?.data?.info || null);
      } catch (err) {
        if (active) setSalonError(err?.response?.data?.message || "No pudimos cargar tu salón.");
      } finally {
        if (active) setSalonLoading(false);
      }
    })();
    return () => {
      active = false;
    };
  }, [isEmployee]);

  const salonId = salon?._id || "";
  const linkedKnown = Boolean(linkedProfessional);

  // Professionals feed the Profesional filter. A linked professional never needs the list (the back
  // forces their scope). Losing it only empties the filter; the statistics do not depend on it.
  useEffect(() => {
    if (!salonId || linkedKnown) return undefined;
    let active = true;
    (async () => {
      try {
        const res = await beautyListProfessionalsApi();
        if (active) setProfessionals(Array.isArray(res?.data?.info) ? res.data.info : []);
      } catch (_) {
        /* the filter stays on "Todas" */
      }
    })();
    return () => {
      active = false;
    };
  }, [salonId, linkedKnown]);

  const range = useMemo(() => resolvePeriod(filters.period, today), [filters.period, today]);
  const comparison = useMemo(
    () => resolveComparison(range, filters.period, filters.compare),
    [range, filters.period, filters.compare]
  );

  const { data, loading, error, comparisonFailed } = useStatsData({
    enabled: Boolean(salonId),
    requestKey: `${salonId}|${filters.professional}`,
    fetchRange: (r) => organizerBeautyStatsApi(buildStatsParams(r, filters.professional)),
    current: range,
    comparison,
    compareMode: filters.compare,
  });

  const shown = data?.current;
  // The response is authoritative once it arrives; `/me` covers the first render.
  const linked = linkedKnown || isLinkedScope(shown);
  const tabs = useMemo(() => (linked ? ALL_TABS.filter((t) => t.id !== "profesionales") : ALL_TABS), [linked]);
  const professionalOptions = useMemo(() => professionalFilterOptions(professionals), [professionals]);

  const handleFilterChange = (patch) => setFilters((prev) => ({ ...prev, ...patch }));

  // One workbook (Resumen, Diario, Horarios, Profesionales, Servicios) with exactly what is on screen: the loaded
  // responses and the label of the filter they were requested with. A linked professional gets no
  // Profesionales sheet (they only have their own numbers).
  const handleExport = () => {
    if (!data) return;
    setExportError(false);
    try {
      const chosen = professionalOptions.find((o) => o.value === filters.professional);
      const professionalLabel = linked ? linkedProfessional?.name || "Tus turnos" : chosen?.label || "Todas";
      const sheets = buildBeautySheets({
        current: data.current,
        comparison: data.comparison,
        salonName: salon?.name || "",
        professionalLabel,
        linked,
      });
      downloadStatsWorkbook(sheets, beautyFileName({ salonName: salon?.name, from: data.current.from, to: data.current.to }));
    } catch (_) {
      setExportError(true);
    }
  };

  if (salonLoading) return <SpinLoading />;

  if (salonError) {
    return (
      <div className="zoco-stats">
        <p className="stats-error" role="alert">
          {salonError}
        </p>
      </div>
    );
  }

  if (!salonId) {
    return (
      <div className="zoco-stats">
        <div className="stats-empty">
          <p>Este negocio todavía no tiene salón.</p>
          <p>Configuralo desde el módulo Beauty para empezar a recibir turnos y ver sus estadísticas.</p>
        </div>
      </div>
    );
  }

  const filterFields = [
    ...periodFields(filters, handleFilterChange),
    ...(linked
      ? []
      : [
          {
            key: "professional",
            label: "Profesional",
            value: filters.professional,
            onChange: (professional) => handleFilterChange({ professional }),
            options: professionalOptions,
            allLabel: "Todas",
          },
        ]),
  ];
  const compareLabel = COMPARE_LABELS[data?.compareMode] || "";
  const bucket = shown ? bucketSize(daysInRange(shown)) : 1;

  return (
    <div className="zoco-stats">
      <StatsTooltipProvider>
        <StatsFilters fields={filterFields}>
          <button type="button" className="stats-btn stats-btn--dark" onClick={handleExport} disabled={!data || loading}>
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
              <path d="M12 4v11M7 10l5 5 5-5M5 20h14" />
            </svg>
            Exportar Excel
          </button>
        </StatsFilters>

        {shown && <StatsRangeCaption shown={shown} comparison={data.comparison} />}
        {linked && (
          <p className="stats-scope-note">
            Estás viendo solo tus turnos.{" "}
            {linkedProfessional?.name
              ? `Tu cuenta de Zoco está vinculada a ${linkedProfessional.name}`
              : "Tu cuenta de Zoco está vinculada a un profesional del salón"}
            , así que la pantalla muestra tus números y tu comisión, no los del resto del salón.
          </p>
        )}
        {exportError && (
          <p className="stats-note" role="status">
            No pudimos generar el Excel. Probá de nuevo.
          </p>
        )}
        {comparisonFailed && (
          <p className="stats-note" role="status">
            No pudimos cargar el período de comparación. Mostramos solo el período elegido.
          </p>
        )}

        <StatsTabs
          tabs={tabs}
          label="Secciones de estadísticas"
          storageKey={TAB_STORAGE_KEY}
          busy={loading}
          className={`stats-content${loading && shown ? " is-refreshing" : ""}`}
        >
          {(tab) => {
            if (error) {
              return (
                <p className="stats-error" role="alert">
                  {error}
                </p>
              );
            }
            if (!shown) return loading ? <SpinLoading /> : null;
            if (tab === "resumen") {
              return <BeautyResumenTab current={shown} comparison={data.comparison} compareLabel={compareLabel} bucket={bucket} linked={linked} />;
            }
            if (tab === "evolucion") {
              return <BeautyEvolucionTab current={shown} comparison={data.comparison} compareMode={data.compareMode} bucket={bucket} />;
            }
            if (tab === "horarios") return <BeautyHorariosTab current={shown} />;
            if (tab === "profesionales") return <BeautyProfesionalesTab current={shown} />;
            return <BeautyServiciosTab current={shown} />;
          }}
        </StatsTabs>
      </StatsTooltipProvider>
    </div>
  );
}
